import { useState, useRef, useEffect } from 'react';
import { Play, Square, Volume2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface VoiceNotePlayerProps {
  src: string;
  compact?: boolean;
  className?: string;
}

export function VoiceNotePlayer({ src, compact = false, className }: VoiceNotePlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;
    
    // Preload metadata
    audio.preload = 'metadata';

    const handleLoadedMetadata = () => {
      // Check if duration is valid (not NaN, not Infinity)
      if (audio.duration && isFinite(audio.duration) && !isNaN(audio.duration)) {
        setDuration(audio.duration);
        setIsLoaded(true);
      }
    };

    const handleDurationChange = () => {
      // Some browsers fire durationchange when duration becomes available
      if (audio.duration && isFinite(audio.duration) && !isNaN(audio.duration)) {
        setDuration(audio.duration);
        setIsLoaded(true);
      }
    };

    const handleCanPlayThrough = () => {
      // Final fallback - duration should definitely be available now
      if (audio.duration && isFinite(audio.duration) && !isNaN(audio.duration)) {
        setDuration(audio.duration);
        setIsLoaded(true);
      }
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      // Also try to get duration here as a fallback
      if (!isLoaded && audio.duration && isFinite(audio.duration) && !isNaN(audio.duration)) {
        setDuration(audio.duration);
        setIsLoaded(true);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleError = () => {
      console.error('Failed to load audio:', src);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('canplaythrough', handleCanPlayThrough);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    // Set source after adding listeners
    audio.src = src;

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('canplaythrough', handleCanPlayThrough);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.pause();
      audio.src = '';
    };
  }, [src]);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const formatTime = (seconds: number | null) => {
    if (seconds === null || !isFinite(seconds) || isNaN(seconds)) {
      return '--:--';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration && duration > 0 ? (currentTime / duration) * 100 : 0;

  if (compact) {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={togglePlay}
        className={cn(
          'gap-1.5',
          isPlaying && 'text-primary',
          className
        )}
      >
        {isPlaying ? (
          <Square className="w-3 h-3" fill="currentColor" />
        ) : (
          <Volume2 className="w-3 h-3" />
        )}
        <span className="text-xs">
          {isPlaying ? formatTime(currentTime) : 'Play'}
        </span>
      </Button>
    );
  }

  return (
    <div className={cn('flex items-center gap-2 p-2 rounded-lg bg-muted/50', className)}>
      <Button
        variant={isPlaying ? 'default' : 'secondary'}
        size="icon"
        className="h-8 w-8 flex-shrink-0"
        onClick={togglePlay}
      >
        {isPlaying ? (
          <Square className="w-3 h-3" fill="currentColor" />
        ) : (
          <Play className="w-3 h-3" />
        )}
      </Button>

      <div className="flex-1 min-w-0">
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <span className="text-xs text-muted-foreground w-12 text-right flex-shrink-0 font-mono">
        {isPlaying ? formatTime(currentTime) : formatTime(duration)}
      </span>
    </div>
  );
}

